import { api } from '@/utils/api';

/** Estado da escola de quem está logado. Âncora de todas as telas da gestão. */
export interface MinhaEscola {
  id: number;
  name: string;
  slug: string | null;
  status: 'ACTIVE' | 'PENDING_REVIEW';
  /** Falso enquanto a escola aguarda revisão: a tela explica em vez de oferecer botão. */
  podeAdministrar: boolean;
  vinculadaEm: string;
  contagens: {
    turmasDaEscola: number;
    turmasDeProfessor: number;
    alunos: number;
    professores: number;
    convitesPendentes: number;
    /** Entrou na escola e nao declarou turma nenhuma. Achado 165. */
    professoresSemTurma: number;
  };
}

export interface ConviteDaEscola {
  id: number;
  email: string;
  turma: { id: number; label: string; year: number; ownership: 'TEACHER' | 'SCHOOL' };
  criadoEm: string;
  aceitoEm: string | null;
  pendente: boolean;
  diasParado: number | null;
}

export const getMinhaEscola = () => api.get<MinhaEscola>('/api/gestao/minha-escola').then((r) => r.data);
/** Só o nome. O identificador é do ADMIN — ver `renomearMinhaEscola`. */
export const renomearMinhaEscola = (name: string) =>
  api.patch<{ id: number; name: string; slug: string | null }>('/api/gestao/minha-escola', { name })
    .then((r) => r.data);

export const getConvites = () => api.get<ConviteDaEscola[]>('/api/gestao/convites').then((r) => r.data);
export const criarTurma = (d: { schoolId: number; label: string; year: number }) =>
  api.post('/api/gestao/turmas', d).then((r) => r.data);
export const convidarProfessor = (classId: number, email: string) =>
  api.post(`/api/gestao/turmas/${classId}/convites`, { email }).then((r) => r.data);
export const revogarConvite = (id: number) => api.delete(`/api/gestao/convites/${id}`);

export interface AlunoEncontrado {
  userId: number;
  nome: string;
  matricula: string;
  turmas: { id: number; label: string }[];
}

export type ResultadoLinha = {
  matricula: string;
  nome: string;
  situacao: 'CADASTRADO' | 'ASSOCIADO' | 'JA_NA_TURMA' | 'ERRO';
  userId?: number;
  /** Usuário e senha só vêm em CADASTRADO, e vêm juntos: é com os dois que o
   *  aluno entra. Ver `utils/credenciais`. */
  usuario?: string;
  senhaProvisoria?: string;
  /** Só vem quando o nome da planilha difere do que está cadastrado. */
  nomeNoCadastro?: string;
  motivo?: string;
};

/** `null` é resposta, não erro: é o que faz a tela oferecer o cadastro. */
export const buscarAluno = (matricula: string) =>
  api.get<AlunoEncontrado | null>('/api/gestao/alunos/buscar', { params: { matricula } }).then((r) => r.data);

export const cadastrarAluno = (d: { classId: number; nome: string; matricula: string }) =>
  api.post('/api/gestao/alunos', d).then((r) => r.data);

export const associarAluno = (d: { classId: number; matricula: string }) =>
  api.post('/api/gestao/alunos/associar', d).then((r) => r.data);

export const importarAlunos = (d: { classId: number; linhas: { nome: string; matricula: string }[] }) =>
  api.post<{ contagem: Record<string, number>; resultados: ResultadoLinha[] }>('/api/gestao/alunos/importar', d)
    .then((r) => r.data);

// ─── Simulado da escola e leitores (fatia 5) ───────────────────────────────

export interface Leitor {
  id: number;
  userId: number;
  createdAt: string;
  user: { id: number; name: string; email: string };
}

export interface DisciplinasDoSimulado {
  total: number;
  multidisciplinar: boolean;
  disciplinas: { id: number; label: string; questoes: number }[];
}

export const tornarDaEscola = (simulationId: number) =>
  api.post(`/api/gestao/simulados/${simulationId}/tornar-da-escola`).then((r) => r.data);

export const desfazerDaEscola = (simulationId: number) =>
  api.post(`/api/gestao/simulados/${simulationId}/desfazer-da-escola`).then((r) => r.data);

export const listarLeitores = (simulationId: number) =>
  api.get<Leitor[]>(`/api/gestao/simulados/${simulationId}/leitores`).then((r) => r.data);

export const adicionarLeitor = (simulationId: number, userId: number) =>
  api.post(`/api/gestao/simulados/${simulationId}/leitores`, { userId }).then((r) => r.data);

export const removerLeitor = (simulationId: number, userId: number) =>
  api.delete(`/api/gestao/simulados/${simulationId}/leitores/${userId}`);

/** Disciplinas presentes nas QUESTÕES do simulado. Serve ao seletor de recorte. */
export const getDisciplinas = (simulationId: number) =>
  api.get<DisciplinasDoSimulado>(`/api/simulations/${simulationId}/disciplinas`).then((r) => r.data);

// ─── Importação de CSV do SIGEDUC ──────────────────────────────────────────

export interface GrupoImportacao {
  rotulo: string;
  codigo: string;
  serie: string;
  dividido: boolean;
  alunos: { matricula: string; nome: string }[];
}

export interface PreviaImportacao {
  ok: boolean;
  erro?: string;
  colunasReconhecidas: string[];
  colunasIgnoradas: string[];
  totalLinhas: number;
  /** VÍNCULOS — participações em turma. É o que o importador cria. */
  totalImportaveis: number;
  /** PESSOAS — matrículas distintas. Menor que os vínculos quando alguém
   *  cursa duas turmas. Ver o serviço do backend. */
  alunosDistintos: number;
  emDuasTurmas: { matricula: string; nome: string; turmas: string[] }[];
  grupos: GrupoImportacao[];
  descartes: { motivo: string; quantidade: number; exemplos: string[] }[];
  /** As ofertas do arquivo, com os matriculados (pessoas) de cada uma (27/09/2026). */
  ofertas?: { oferta: string; matriculados: number; linhas: number }[];
}

export interface ResultadoGrupo {
  turma: { id: number; label: string; criada: boolean };
  contagem: Record<string, number>;
  resultados: ResultadoLinha[];
}

/** Só lê e agrupa. Não escreve nada. */
export const previaImportacao = (texto: string) =>
  api.post<PreviaImportacao>('/api/gestao/importacao/previa', { texto }).then((r) => r.data);

/**
 * UM BLOCO de alunos de uma turma. O motivo do bloco está no serviço do backend.
 *
 * `classId` ausente no primeiro bloco — ele cria ou acha a turma e devolve o id;
 * os seguintes o informam, e assim o custo fixo é pago uma vez só.
 */
export const importarGrupo = (g: {
  rotulo: string;
  codigo: string;
  ano: number;
  alunos: { matricula: string; nome: string }[];
  classId?: number;
}) => api.post<ResultadoGrupo>('/api/gestao/importacao/turma', g).then((r) => r.data);


// ─── Reconciliação com o portal (27/09/2026) ───────────────────────────────

export interface LinhaDaReconciliacao {
  indice: number;
  matricula: string;
  nome: string;
  /** Turmas da escola em que está hoje. */
  de: string[];
  /** Turmas em que fica. */
  para: string[];
  /** A situação do Sigeduc, quando encerra. */
  motivo?: string;
}

export interface Reconciliacao {
  previaId: string;
  ano: number;
  escola: string;
  arquivo: { totalLinhas: number; alunosDistintos: number; descartes: { motivo: string; quantidade: number; exemplos: string[] }[] };
  totalOperacoes: number;
  semMudanca: number;
  turmasNovas: string[];
  entramNovos: LinhaDaReconciliacao[];
  entram: LinhaDaReconciliacao[];
  mudam: LinhaDaReconciliacao[];
  reativam: LinhaDaReconciliacao[];
  encerram: LinhaDaReconciliacao[];
  ausentes: { matricula: string; nome: string; turmas: string[] }[];
  /** Matriculados só em oferta não escolhida, e que estão no portal: nada muda. */
  foraDasOfertas: { matricula: string; nome: string; turmas: string[] }[];
  ofertas: { oferta: string; matriculados: number; linhas: number; escolhida: boolean }[];
  bloqueios: { rotulo: string; motivo: string; alunos: number }[];
  alunosBloqueados: { matricula: string; nome: string; turmas: string[] }[];
  avisos: {
    nomesDivergentes: { matricula: string; noArquivo: string; noPortal: string }[];
    emDuasDoMesmoSimulado: { nome: string; turmas: string[]; simulados: string[] }[];
  };
}

export interface ResultadoDaOperacao {
  indice: number;
  tipo: 'ENTRA_NOVO' | 'ENTRA' | 'MUDA' | 'REATIVA' | 'ENCERRA';
  matricula: string;
  nome: string;
  estado: 'ok' | 'pulado' | 'erro';
  motivo?: string;
  usuario?: string;
  senhaProvisoria?: string;
}

/** Compara o arquivo com o portal. NÃO grava nada — devolve a prévia. */
export const reconciliarImportacao = (texto: string, ano: number, ofertas?: string[]) =>
  api.post<Reconciliacao>('/api/gestao/importacao/reconciliar', { texto, ano, ofertas }).then((r) => r.data);

/** Aplica um bloco da prévia confirmada. */
export const aplicarReconciliacao = (previaId: string, desde: number, quantos: number) =>
  api
    .post<{ resultados: ResultadoDaOperacao[]; total: number; aplicadas: number }>('/api/gestao/importacao/aplicar', { previaId, desde, quantos })
    .then((r) => r.data);

// ─── Convite de ESCOLA (professor sem turma) ───────────────────────────────

export interface ResultadoConviteEscola {
  email: string;
  situacao: 'VINCULADO' | 'CONVIDADO' | 'JA_ERA' | 'ERRO';
  motivo?: string;
  /** Contas com o MESMO local part noutro domínio institucional. O sistema
   *  mostra a coincidência; quem decide é a gestão. Ver o serviço. */
  parecidas?: { email: string; nome: string }[];
}

/** Extrai os endereços do texto colado. Não escreve nada. */
export const previaConviteEscola = (texto: string) =>
  api.post<{ emails: string[]; semEmail: string[] }>('/api/gestao/professores/previa', { texto })
    .then((r) => r.data);

export const convidarProfessoresDaEscola = (emails: string[]) =>
  api.post<{ escola: { id: number; name: string }; resultados: ResultadoConviteEscola[] }>(
    '/api/gestao/professores/convidar', { emails }
  ).then((r) => r.data);

// ─── O lado do PROFESSOR ───────────────────────────────────────────────────

export interface Disciplina { id: number; label: string }

export interface EscolaDoProfessor {
  id: number;
  name: string;
  status: string;
  turmas: {
    id: number; label: string; year: number; alunos: number; leciono: boolean;
    /** Vazio é resposta: a disciplina NÃO é obrigatória ao marcar a turma. */
    disciplinas: Disciplina[];
  }[];
}

export const minhasEscolasComoProfessor = () =>
  api.get<{ escolas: EscolaDoProfessor[]; disciplinas: Disciplina[] }>(
    '/api/professor-escola/minhas-escolas'
  ).then((r) => r.data);

export const declararMinhaDisciplina = (classId: number, subjectId: number) =>
  api.post(`/api/professor-escola/turmas/${classId}/disciplinas`, { subjectId }).then((r) => r.data);

export const removerMinhaDisciplina = (classId: number, subjectId: number) =>
  api.delete(`/api/professor-escola/turmas/${classId}/disciplinas/${subjectId}`).then((r) => r.data);

/** As disciplinas que EU leciono nesta turma — o recorte inicial do relatório. */
export const minhasDisciplinasNaTurma = (classId: number) =>
  api.get<Disciplina[]>(`/api/professor-escola/turmas/${classId}/minhas-disciplinas`).then((r) => r.data);

export const declararQueLeciona = (classId: number, leciono: boolean) =>
  api.patch(`/api/professor-escola/turmas/${classId}/leciono`, { leciono }).then((r) => r.data);


// ─── Professores da escola: lotação num lugar só ───────────────────────────

export interface TurmaDoProfessor {
  id: number;
  label: string;
  year: number;
  ownership: 'TEACHER' | 'SCHOOL';
  alunos: number;
  /** A lotação dele nesta turma. Vazia nos vínculos anteriores a esta fatia. */
  disciplinas: { id: number; label: string }[];
  /** Quem criou o vínculo. Decide a PALAVRA na tela: quem marcou "desvincula",
   *  quem foi convidado tem o convite "revogado". Ver o serviço. */
  origem: 'professor' | 'gestao';
  conviteId: number | null;
}

export interface ProfessoresDaEscola {
  escola: { id: number; name: string };
  vinculados: {
    userId: number;
    nome: string;
    email: string;
    desde: string;
    turmas: TurmaDoProfessor[];
  }[];
  convidados: { id: number; email: string; criadoEm: string; diasParado: number }[];
  turmasAtribuiveis: { id: number; label: string; year: number; alunos: number }[];
  disciplinas: { id: number; label: string }[];
}

export const getProfessoresDaEscola = () =>
  api.get<ProfessoresDaEscola>('/api/gestao/professores').then((r) => r.data);

export const desvincularProfessorDaTurma = (professorId: number, classId: number) =>
  api.delete(`/api/gestao/professores/${professorId}/turmas/${classId}`).then((r) => r.data);

export const declararLotacao = (professorId: number, classId: number, subjectId: number) =>
  api.post(`/api/gestao/professores/${professorId}/turmas/${classId}/disciplinas`, { subjectId }).then((r) => r.data);

export const removerLotacao = (professorId: number, classId: number, subjectId: number) =>
  api.delete(`/api/gestao/professores/${professorId}/turmas/${classId}/disciplinas/${subjectId}`).then((r) => r.data);

/** As minhas disciplinas na turma deste aluno, neste simulado — para a correção. */
export const minhasDisciplinasNoAtendimento = (simulationId: number, studentId: number) =>
  api.get<Disciplina[]>(`/api/professor-escola/simulados/${simulationId}/alunos/${studentId}/minhas-disciplinas`)
    .then((r) => r.data);

// ── E-mails institucionais dos estudantes (relação da Secretaria) ──────────────
export interface TrocaDeEmail { userId: number; nome: string; matricula: string; de: string; para: string }
export interface PreviaDeEmails {
  previaId: string;
  escola: string;
  linhas: number;
  estudantesNaEscola: number;
  recebem: TrocaDeEmail[];
  outroEmail: TrocaDeEmail[];
  jaIguais: number;
  naoCasam: { ra: string }[];
  encerrados: { nome: string; matricula: string }[];
  conflitos: (TrocaDeEmail & { usadoPor: string })[];
  invalidos: { ra: string; email: string }[];
  repetidos: { ra: string; email: string; motivo: string }[];
}

/** Compara a relação com o portal. NÃO grava nada. */
export const previaDeEmails = (texto: string) =>
  api.post<PreviaDeEmails>('/api/gestao/importacao/emails/previa', { texto }).then((r) => r.data);

/** Aplica a prévia confirmada. */
export const aplicarEmails = (previaId: string, substituirOutros: boolean) =>
  api.post<{ aplicadas: number; puladas: number; total: number }>('/api/gestao/importacao/emails/aplicar', { previaId, substituirOutros }).then((r) => r.data);
